import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import '../Admin/Users.css';
import { formatAppDate, toLocalDateKey } from '../../utils/dateUtils';

const getPeriodDates = (p) => {
  const now = new Date();
  let start, end;
  switch (p) {
    case 'month': {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      break;
    }
    case 'last_month': {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    }
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      start = new Date(now.getFullYear(), q * 3, 1);
      end = new Date(now.getFullYear(), q * 3 + 3, 0);
      break;
    }
    case 'year': {
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), 11, 31);
      break;
    }
    default:
      return null;
  }
  const fmt = toLocalDateKey;
  return { start: fmt(start), end: fmt(end) };
};

const FormulationHistory = ({ onSelect, selectedId }) => {
  const { t } = useTranslation('production');
  const [period, setPeriod] = useState('month');
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/formulations/orders?page_size=200');
      if (useCustomDates && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      } else if (period !== 'all') {
        const dates = getPeriodDates(period);
        if (dates) {
          url += `&start_date=${dates.start}&end_date=${dates.end}`;
        }
      }
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (e) {
      console.error('Error fetching formulation history:', e);
    } finally {
      setLoading(false);
    }
  }, [period, useCustomDates, startDate, endDate, statusFilter, searchTerm]);

  useEffect(() => {
    if (!useCustomDates) {
      fetchOrders();
    }
  }, [period, statusFilter, useCustomDates]);

  useEffect(() => {
    if (useCustomDates && startDate && endDate) {
      fetchOrders();
    }
  }, [startDate, endDate, useCustomDates]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') fetchOrders();
  };

  const getStatusBadge = (status) => {
    const map = {
      DRAFT: 'status-warning',
      IN_PROGRESS: 'status-active',
      COMPLETED: 'status-success',
      CANCELLED: 'status-error',
      VOIDED: 'status-error',
    };
    return <span className={`status-badge ${map[status] || 'status-inactive'}`}>{status ? t('formulationOrders.status.' + status, { defaultValue: status }) : status}</span>;
  };

  const formatDate = (d) => (d ? formatAppDate(d) : '-');
  const formatQty = (val) => (val != null ? parseFloat(val).toFixed(2) : '-');

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>🕐 {t('formulationHistory.title')}</h2>
      </div>

      {/* Date filter controls */}
      <div className="list-controls" style={{ flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '6px', padding: '2px' }}>
          <button
            style={{
              padding: '4px 10px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: !useCustomDates ? '600' : '400',
              background: !useCustomDates ? 'var(--bg-primary)' : 'transparent',
              color: !useCustomDates ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: !useCustomDates ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
            onClick={() => setUseCustomDates(false)}
          >
            {t('formulationHistory.preset')}
          </button>
          <button
            style={{
              padding: '4px 10px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: useCustomDates ? '600' : '400',
              background: useCustomDates ? 'var(--bg-primary)' : 'transparent',
              color: useCustomDates ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: useCustomDates ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
            onClick={() => setUseCustomDates(true)}
          >
            {t('formulationHistory.custom')}
          </button>
        </div>

        {!useCustomDates ? (
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="filter-select"
          >
            <option value="month">{t('formulationHistory.periods.thisMonth')}</option>
            <option value="last_month">{t('formulationHistory.periods.lastMonth')}</option>
            <option value="quarter">{t('formulationHistory.periods.thisQuarter')}</option>
            <option value="year">{t('formulationHistory.periods.thisYear')}</option>
            <option value="all">{t('formulationHistory.periods.allTime')}</option>
          </select>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('formulationHistory.from')}:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ fontSize: '12px', padding: '4px 6px', border: '1px solid var(--border-color)', borderRadius: '4px', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
            />
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('formulationHistory.to')}:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ fontSize: '12px', padding: '4px 6px', border: '1px solid var(--border-color)', borderRadius: '4px', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
            />
          </div>
        )}

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">{t('formulationHistory.allStatus')}</option>
          <option value="DRAFT">{t('formulationOrders.status.DRAFT')}</option>
          <option value="IN_PROGRESS">{t('formulationOrders.status.IN_PROGRESS')}</option>
          <option value="COMPLETED">{t('formulationOrders.status.COMPLETED')}</option>
          <option value="CANCELLED">{t('formulationOrders.status.CANCELLED')}</option>
          <option value="VOIDED">{t('formulationOrders.status.VOIDED')}</option>
        </select>

        <div className="search-box" style={{ flex: 1, minWidth: '150px' }}>
          <input
            type="text"
            placeholder={t('formulationHistory.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearch}
          />
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading">{t('formulationHistory.loading')}</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('formulationHistory.columns.orderNumber')}</th>
                <th>{t('formulationHistory.columns.recipe')}</th>
                <th>{t('formulationHistory.columns.outputItem')}</th>
                <th>{t('formulationHistory.columns.batchMultiplier')}</th>
                <th>{t('formulationHistory.columns.actualQty')}</th>
                <th>{t('common:ui.status')}</th>
                <th>{t('formulationHistory.columns.created')}</th>
                <th>{t('formulationHistory.columns.completed')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan="8" className="no-data">{t('formulationHistory.empty')}</td></tr>
              ) : (
                orders.map((o) => (
                  <tr
                    key={o.formulation_order_id}
                    className={selectedId === o.formulation_order_id ? 'selected' : ''}
                    onClick={() => onSelect(o.formulation_order_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{o.order_number}</strong></td>
                    <td>{o.recipe_name || '-'}</td>
                    <td>{o.output_item_name || '-'}</td>
                    <td>{o.batch_multiplier != null ? parseFloat(o.batch_multiplier).toFixed(2) : '-'}</td>
                    <td>{formatQty(o.actual_output_quantity)}</td>
                    <td>{getStatusBadge(o.status)}</td>
                    <td>{formatDate(o.created_date)}</td>
                    <td>{formatDate(o.completed_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FormulationHistory;
